import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Globe, Facebook, Twitter, Linkedin, Instagram, Music, Youtube, Palette } from "lucide-react";
import { validateSocialUrl } from "@/lib/validateUrl";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (No DST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "America/Toronto", label: "Toronto (ET)" },
  { value: "Europe/London", label: "London (GMT)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
];

export const OrganizationSettings = () => {
  const [timezone, setTimezone] = useState<string>("America/New_York");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsId, setSettingsId] = useState<string>("");
  
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: "",
    twitter_url: "",
    linkedin_url: "",
    instagram_url: "",
    tiktok_url: "",
    youtube_url: "",
    pinterest_url: "",
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from('organization_settings')
      .select('*')
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching settings:', error);
      toast.error("Failed to load organization settings");
    } else if (data) {
      setTimezone(data.timezone);
      setSettingsId(data.id);
      setSocialLinks({
        facebook_url: data.facebook_url || "",
        twitter_url: data.twitter_url || "",
        linkedin_url: data.linkedin_url || "",
        instagram_url: data.instagram_url || "",
        tiktok_url: data.tiktok_url || "",
        youtube_url: data.youtube_url || "",
        pinterest_url: data.pinterest_url || "",
      });
    }
    setLoading(false);
  };

  const handleTimezoneChange = async (newTimezone: string) => {
    setTimezone(newTimezone);

    const { error } = await supabase
      .from('organization_settings')
      .update({ timezone: newTimezone, updated_at: new Date().toISOString() })
      .eq('id', settingsId);

    if (error) {
      console.error('Error updating timezone:', error);
      toast.error("Failed to update timezone");
    } else {
      toast.success("Timezone updated successfully");
    }
  };

  const handleSocialLinkChange = (platform: string, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [platform]: value }));
    
    // Clear error when user starts typing
    if (errors[platform]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[platform];
        return newErrors;
      });
    }
  };

  const validateSocialLinks = () => {
    const newErrors: Record<string, string> = {};
    
    Object.entries(socialLinks).forEach(([platform, url]) => {
      if (url) {
        const platformName = platform.replace('_url', '');
        const error = validateSocialUrl(url, platformName);
        if (error) {
          newErrors[platform] = error;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSocialLinks = async () => {
    if (!validateSocialLinks()) {
      toast.error("Please fix the URL errors before saving");
      return;
    }

    setSaving(true);
    
    const { error } = await supabase
      .from('organization_settings')
      .update({
        ...socialLinks,
        updated_at: new Date().toISOString()
      })
      .eq('id', settingsId);

    if (error) {
      console.error('Error updating social links:', error);
      toast.error("Failed to update social media links");
    } else {
      toast.success("Social media links updated successfully");
    }
    
    setSaving(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading settings...</p>
        </CardContent>
      </Card>
    );
  }

  const socialPlatforms = [
    { key: "facebook_url", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/yourpage" },
    { key: "twitter_url", label: "X (Twitter)", icon: Twitter, placeholder: "https://x.com/yourhandle" },
    { key: "linkedin_url", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/company/yourcompany" },
    { key: "instagram_url", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/yourprofile" },
    { key: "tiktok_url", label: "TikTok", icon: Music, placeholder: "https://tiktok.com/@yourprofile" },
    { key: "youtube_url", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@yourchannel" },
    { key: "pinterest_url", label: "Pinterest", icon: Palette, placeholder: "https://pinterest.com/yourprofile" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Organization Settings
          </CardTitle>
          <CardDescription>
            Configure your business timezone and regional preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="timezone">Business Timezone</Label>
            <Select value={timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger id="timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              All appointments and business hours will use this timezone
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Social Media Links</CardTitle>
          <CardDescription>
            Add your social media profiles to display in the website footer. Leave blank to hide.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {socialPlatforms.map(({ key, label, icon: Icon, placeholder }) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={key} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </Label>
              <Input
                id={key}
                type="url"
                value={socialLinks[key as keyof typeof socialLinks]}
                onChange={(e) => handleSocialLinkChange(key, e.target.value)}
                placeholder={placeholder}
                className={errors[key] ? "border-destructive" : ""}
              />
              {errors[key] && (
                <p className="text-sm text-destructive">{errors[key]}</p>
              )}
            </div>
          ))}
          
          <Button 
            onClick={handleSaveSocialLinks} 
            disabled={saving}
            className="w-full"
          >
            {saving ? "Saving..." : "Save Social Media Links"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
